import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { CopyCheck, Phone, User, Loader, Archive, ShieldCheck, AlertTriangle, GitMerge, Users } from 'lucide-react';
import { useSmartTranslation } from '../hooks/useStudentLabel';
import useAuthStore from '../store/authStore';
import { useToastContext } from '../context/ToastContext';
import EnterpriseModal from './EnterpriseModal';
import api from '../services/api';
import { findDuplicateClusters } from '../utils/duplicateDetection';
import MergeClientsModal from './Sport/MergeClientsModal';

/**
 * Duplicate-student detector. Given a set of students it clusters likely
 * duplicates (same phone or same name, normalised) and lets the admin keep one
 * record per cluster and clear the rest.
 *
 * Two modes:
 *  - mode="global"  (Students page): cleared records are archived
 *                   (soft-deleted) from the students collection.
 *  - mode="group"   (a group's roster): cleared records are removed from the
 *                   group, and — when "also archive" is on (default) — archived
 *                   from the students collection too.
 *
 * Props:
 *  - isOpen, onClose
 *  - mode             'global' | 'group'
 *  - groupId          required for group mode
 *  - students         pre-loaded list (group members or flat students). Each may
 *                     be a flat student or a group_students row ({ studentId, student }).
 *  - loadStudents()   optional async loader; when given, fetched on open instead
 *                     of using `students` (used by the Students page to scan all).
 *  - isTherapyCenter  switches student/client wording
 *  - onResolved()     called after any change so the parent can refresh
 *
 * Learning centers (sport first) also get "Merge" per cluster (MergeClientsModal): the files are
 * folded into one instead of archiving one with its history. There the
 * recommended keeper is the file with a signed intake (else the newest), and a
 * cluster on one phone with different first names is flagged "maybe siblings".
 */
export default function DuplicateDetectionModal({
  isOpen, onClose, mode = 'global', groupId, students, loadStudents, isTherapyCenter = false, onResolved,
}) {
  const { i18n } = useTranslation();
  const t = useSmartTranslation();
  const isRTL = i18n.language === 'ar' || i18n.language === 'he';
  const { success, error: showError } = useToastContext();
  // Same rule as useSportCenter (hooks/useStudentLabel), read from the store
  // directly so callers' tests that stub that hook module keep working.
  const isSport = useAuthStore((s) => s.centerType === 'learning' && s.learningSubType === 'sport');
  // Merge: every learning center (useLearningCenter's rule; not caffe, not clinics).
  const canMerge = useAuthStore((s) => s.centerType === 'learning' && s.learningSubType !== 'caffe') && !isTherapyCenter;
  const [mergeCluster, setMergeCluster] = useState(null);

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]); // normalised, deduped-by-id student records
  const [keepIds, setKeepIds] = useState(() => new Set());   // chosen "keep" per cluster
  const [removeIds, setRemoveIds] = useState(() => new Set()); // records to clear
  const [alsoArchive, setAlsoArchive] = useState(true);
  const [applying, setApplying] = useState(false);

  const noun = isTherapyCenter ? t('common.clients', 'Clients') : t('common.students', 'Students');

  // Flatten whatever shape we were handed (group_students row or flat student)
  // into a uniform record the detector understands, dropping duplicate ids.
  const normalize = useCallback((list) => {
    const seen = new Set();
    const out = [];
    for (const item of list || []) {
      const s = item.student || item;
      const id = item.studentId || s.id || item.id;
      if (id == null || seen.has(id)) continue;
      seen.add(id);
      out.push({
        id,
        firstName: s.firstName, middleName: s.middleName, lastName: s.lastName,
        phone: s.phone, parentPhone: s.parentPhone, guardianMobile: s.guardianMobile, email: s.email,
        dateOfBirth: s.dateOfBirth, nationalId: s.nationalId, guardianName: s.guardianName,
        notes: s.notes, createdAt: s.createdAt, intakeId: s.intakeId || null, source: s.source || null,
      });
    }
    return out;
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;
    let cancelled = false;
    setApplying(false);
    setAlsoArchive(true);
    if (loadStudents) {
      setLoading(true);
      Promise.resolve(loadStudents())
        .then((list) => { if (!cancelled) setRows(normalize(list)); })
        .catch(() => { if (!cancelled) { setRows([]); showError(t('errors.generic', 'Something went wrong')); } })
        .finally(() => { if (!cancelled) setLoading(false); });
    } else {
      setRows(normalize(students));
    }
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, students, loadStudents, normalize]);

  const clusters = useMemo(() => findDuplicateClusters(rows, isSport ? { prefer: 'intake' } : undefined), [rows, isSport]);

  // A merge archived `fromId` into `keepId`: drop it so the clusters recompute.
  const handleMerged = (keepId, fromId) => {
    setRows((prev) => prev.filter((s) => s.id !== fromId));
    setMergeCluster(null);
    onResolved?.();
  };

  // Default selection: keep each cluster's recommended primary, clear the rest.
  useEffect(() => {
    const keep = new Set();
    const remove = new Set();
    for (const c of clusters) {
      keep.add(c.primaryId);
      for (const m of c.members) if (m.id !== c.primaryId) remove.add(m.id);
    }
    setKeepIds(keep);
    setRemoveIds(remove);
  }, [clusters]);

  // Choose which record in a cluster to keep: the others become removable, and
  // any of them previously kept is now a removal candidate (re-checked).
  const chooseKeep = (cluster, id) => {
    setKeepIds((prev) => {
      const next = new Set(prev);
      for (const m of cluster.members) next.delete(m.id);
      next.add(id);
      return next;
    });
    setRemoveIds((prev) => {
      const next = new Set(prev);
      for (const m of cluster.members) {
        if (m.id === id) next.delete(m.id);
        else next.add(m.id);
      }
      return next;
    });
  };

  // Toggle whether a non-kept record is actually cleared (lets the admin spare a
  // record that only looks like a duplicate).
  const toggleRemove = (id) => {
    if (keepIds.has(id)) return; // the kept record is never removable
    setRemoveIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const removeCount = removeIds.size;

  const apply = async () => {
    const ids = Array.from(removeIds);
    if (ids.length === 0) return;
    setApplying(true);
    let removed = 0;
    let failed = 0;
    const archiveToo = mode === 'global' || alsoArchive;
    const results = await Promise.allSettled(ids.map(async (id) => {
      if (mode === 'group') {
        await api.delete(`/groups/${groupId}/students/${id}`);
        if (archiveToo) await api.delete(`/students/${id}`);
      } else {
        await api.delete(`/students/${id}`);
      }
    }));
    results.forEach((r) => { r.status === 'fulfilled' ? removed++ : failed++; });

    if (removed > 0) {
      success(mode === 'group'
        ? t('duplicates.removedGroupToast', { count: removed, defaultValue: `${removed} duplicate ${noun} cleared` })
        : t('duplicates.archivedToast', { count: removed, defaultValue: `${removed} duplicate ${noun} archived` }));
    }
    if (failed > 0) {
      showError(t('duplicates.someFailedToast', { count: failed, defaultValue: `${failed} could not be processed` }));
    }
    // Drop cleared rows so the cluster view recomputes against what remains.
    setRows((prev) => prev.filter((s) => !removeIds.has(s.id)));
    setApplying(false);
    onResolved?.();
  };

  const fullName = (s) => [s.firstName, s.middleName, s.lastName].filter(Boolean).join(' ') || '—';
  const initial = (s) => (s.firstName || s.lastName || '?').trim().charAt(0).toUpperCase();

  const reasonLabel = (reasons) => {
    const has = (r) => reasons.includes(r);
    if (has('phone') && has('name')) return t('duplicates.reasonBoth', 'Same name & phone');
    if (has('phone')) return t('duplicates.reasonPhone', 'Same phone number');
    if (has('name')) return t('duplicates.reasonName', 'Same name');
    return t('duplicates.reasonGeneric', 'Possible duplicate');
  };

  const footer = (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', width: '100%', flexWrap: 'wrap' }}>
      {mode === 'group' && (
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.8125rem', color: 'var(--color-text-secondary)', cursor: 'pointer' }}>
          <input type="checkbox" checked={alsoArchive} onChange={(e) => setAlsoArchive(e.target.checked)} />
          <Archive size={14} /> {t('duplicates.alsoArchive', 'Also archive from students')}
        </label>
      )}
      <div style={{ flex: 1, minWidth: '80px' }} />
      <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
        {t('duplicates.selectedToClear', { count: removeCount, defaultValue: `${removeCount} to clear` })}
      </span>
      <button
        onClick={apply}
        disabled={removeCount === 0 || applying}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
          padding: '0.625rem 1.25rem', borderRadius: '0.625rem', border: 'none',
          backgroundColor: removeCount && !applying ? '#dc2626' : 'var(--color-border)',
          color: '#fff', fontWeight: 600, fontSize: '0.875rem',
          cursor: removeCount && !applying ? 'pointer' : 'not-allowed',
        }}
      >
        {applying ? <Loader size={16} style={{ animation: 'dupSpin 1s linear infinite' }} /> : <CopyCheck size={16} />}
        {mode === 'group'
          ? t('duplicates.applyGroup', 'Remove duplicates')
          : t('duplicates.applyGlobal', 'Archive duplicates')}
        {removeCount > 0 ? ` (${removeCount})` : ''}
      </button>
    </div>
  );

  return (
    <EnterpriseModal
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
      title={t('duplicates.title', 'Detect duplicates')}
      subtitle={t('duplicates.subtitle', 'Matched by phone number or name — keep one, clear the rest')}
      titleIcon={<CopyCheck size={18} />}
      footer={footer}
      bodyPadding="0"
    >
      <style>{`@keyframes dupSpin { to { transform: rotate(360deg); } }`}</style>

      <div style={{ padding: '1rem 1.25rem' }}>
        {loading ? (
          <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--color-text-tertiary)' }}>
            <Loader size={24} style={{ animation: 'dupSpin 1s linear infinite' }} />
            <div style={{ marginTop: '0.5rem', fontSize: '0.875rem' }}>{t('duplicates.scanning', 'Scanning…')}</div>
          </div>
        ) : clusters.length === 0 ? (
          <div style={{ padding: '3rem 1rem', textAlign: 'center', color: 'var(--color-text-tertiary)' }}>
            <ShieldCheck size={32} style={{ opacity: 0.5, marginBottom: '0.5rem', color: '#16a34a' }} />
            <div style={{ fontSize: '0.9375rem', fontWeight: 600, color: 'var(--color-text-secondary)' }}>
              {t('duplicates.noneFound', 'No duplicates found')}
            </div>
            <div style={{ fontSize: '0.8125rem', marginTop: '0.25rem' }}>
              {t('duplicates.noneFoundHint', { count: rows.length, defaultValue: `Scanned ${rows.length} ${noun}.` })}
            </div>
          </div>
        ) : (
          <>
            <div style={{
              display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.875rem',
              padding: '0.625rem 0.75rem', borderRadius: '0.625rem',
              backgroundColor: 'var(--color-warning-light, #fef3c7)', color: 'var(--color-warning-dark, #92400e)',
              fontSize: '0.8125rem',
            }}>
              <AlertTriangle size={16} style={{ flexShrink: 0 }} />
              {t('duplicates.foundBanner', {
                count: clusters.length,
                defaultValue: `${clusters.length} possible duplicate group(s). Review the keeper (✓) in each before clearing.`,
              })}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.875rem' }}>
              {clusters.map((cluster, ci) => (
                <div key={cluster.id} style={{ border: '1px solid var(--color-border)', borderRadius: '0.75rem', overflow: 'hidden' }}>
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 0.75rem',
                    backgroundColor: 'var(--color-background)', borderBottom: '1px solid var(--color-border)',
                  }}>
                    <span style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--color-text-tertiary)' }}>#{ci + 1}</span>
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.75rem', fontWeight: 600,
                      color: 'var(--color-primary-dark)', backgroundColor: 'var(--color-primary-light)',
                      borderRadius: '999px', padding: '0.1rem 0.5rem',
                    }}>
                      {cluster.reasons.includes('phone') ? <Phone size={11} /> : <User size={11} />}
                      {reasonLabel(cluster.reasons)}
                    </span>
                    <span style={{ fontSize: '0.75rem', color: 'var(--color-text-tertiary)' }}>· {cluster.members.length}</span>
                    {canMerge && cluster.maybeSiblings && (
                      <span data-testid={`dup-siblings-${cluster.id}`} style={{
                        display: 'inline-flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.75rem', fontWeight: 600,
                        color: 'var(--color-warning-dark, #92400e)', backgroundColor: 'var(--color-warning-light, #fef3c7)',
                        borderRadius: '999px', padding: '0.1rem 0.5rem',
                      }}>
                        <Users size={11} /> {t('duplicates.maybeSiblings', 'Maybe siblings')}
                      </span>
                    )}
                    {canMerge && (
                      <button
                        type="button"
                        onClick={() => setMergeCluster(cluster)}
                        disabled={applying}
                        data-testid={`dup-merge-${cluster.id}`}
                        title={t('duplicates.mergeHint', 'Fold the files into one — sessions, payments and files move to the kept file')}
                        style={{
                          marginInlineStart: 'auto', display: 'inline-flex', alignItems: 'center', gap: '0.35rem',
                          minHeight: '32px', padding: '0.25rem 0.75rem', borderRadius: '0.5rem',
                          border: '1px solid var(--color-primary)', backgroundColor: 'var(--color-surface)',
                          color: 'var(--color-primary-dark)', fontSize: '0.8125rem', fontWeight: 600, cursor: 'pointer',
                        }}
                      >
                        <GitMerge size={14} /> {t('duplicates.merge', 'Merge')}
                      </button>
                    )}
                  </div>

                  <div>
                    {cluster.members.map((s) => {
                      const isKeep = keepIds.has(s.id);
                      const isRemove = removeIds.has(s.id);
                      return (
                        <div key={s.id} style={{
                          display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.5rem 0.75rem',
                          borderBottom: '1px solid var(--color-border)',
                          backgroundColor: isKeep ? 'var(--color-success-light, #dcfce7)' : (isRemove ? 'var(--color-danger-light, #fee2e2)' : 'var(--color-surface)'),
                        }}>
                          {/* Keep radio */}
                          <label title={t('duplicates.keepThis', 'Keep this one')} style={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer' }}>
                            <input
                              type="radio"
                              name={`keep-${cluster.id}`}
                              checked={isKeep}
                              onChange={() => chooseKeep(cluster, s.id)}
                              style={{ cursor: 'pointer' }}
                            />
                          </label>
                          <span style={{
                            width: '2rem', height: '2rem', borderRadius: '50%', flexShrink: 0,
                            backgroundColor: 'var(--color-primary-light)', color: 'var(--color-primary-dark)',
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: '0.8125rem', fontWeight: 700,
                          }}>{initial(s)}</span>
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <div style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--color-text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {fullName(s)}
                              {isKeep && (
                                <span style={{ marginInlineStart: '0.4rem', fontSize: '0.6875rem', fontWeight: 700, color: 'var(--color-success-dark, #15803d)' }}>
                                  ✓ {t('duplicates.keepBadge', 'Keep')}
                                </span>
                              )}
                            </div>
                            {(s.phone || s.parentPhone) && (
                              <div style={{ fontSize: '0.75rem', color: 'var(--color-text-tertiary)', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                                <Phone size={11} /> <span dir="ltr">{s.phone || s.parentPhone}</span>
                              </div>
                            )}
                          </div>
                          {/* Clear toggle (disabled on the kept record) */}
                          {!isKeep && (
                            <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.75rem', color: isRemove ? '#dc2626' : 'var(--color-text-tertiary)', cursor: 'pointer' }}>
                              <input type="checkbox" checked={isRemove} onChange={() => toggleRemove(s.id)} style={{ cursor: 'pointer' }} />
                              {t('duplicates.clear', 'Clear')}
                            </label>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
      {canMerge && mergeCluster && (() => {
        const primary = mergeCluster.members.find((m) => m.id === mergeCluster.primaryId) || mergeCluster.members[0];
        const others = mergeCluster.members.filter((m) => m.id !== primary.id);
        return (
          <MergeClientsModal
            isOpen
            onClose={() => setMergeCluster(null)}
            student={primary}
            candidates={others}
            initialOtherId={others.length === 1 ? others[0].id : null}
            onMerged={handleMerged}
          />
        );
      })()}
    </EnterpriseModal>
  );
}
